import { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Collapse from "@mui/material/Collapse";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import { academyService } from "api";

const COLUMN_DEFS = [
  { header: "FullName*",      width: 25 },
  { header: "DateOfBirth*",   width: 15 },
  { header: "ParentName*",    width: 25 },
  { header: "ParentPhone*",   width: 15 },
  { header: "AlternatePhone", width: 15 },
  { header: "ParentEmail",    width: 28 },
  { header: "Address*",       width: 30 },
  { header: "MonthlyFee*",    width: 13 },
  { header: "FeePlan*",       width: 18 },
  { header: "BatchName*",     width: 18 },
  { header: "AdmissionDate*", width: 16 },
  { header: "Status",         width: 12 },
  { header: "Notes",          width: 30 },
  { header: "PaidUpToMonth*", width: 16 },
];

const SAMPLE_ROWS = [
  ["Rahul Sharma", "15-06-2015", "Ravi Sharma",  "9876543210", "",           "ravi@email.com", "123 MG Road Mumbai", 800, "Standard",    "U10 Morning", "01-04-2026", "Active", "Good player", "03-2026"],
  ["Priya Patel",  "22-03-2016", "Suresh Patel", "8765432109", "7654321098", "",               "456 Park Street",    600, "Scholarship", "U8 Evening",  "01-04-2026", "Trial",  "",            "04-2026"],
];

const DD_MM_YYYY = /^\d{2}-\d{2}-\d{4}$/;

function parseDDMMYYYY(str) {
  const [dd, mm, yyyy] = str.split("-");
  return new Date(`${yyyy}-${mm}-${dd}`);
}

function toISODate(ddmmyyyy) {
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  return `${yyyy}-${mm}-${dd}`;
}

function excelDateToString(value) {
  if (value instanceof Date) {
    const dd = String(value.getDate()).padStart(2, "0");
    const mm = String(value.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${value.getFullYear()}`;
  }
  return String(value ?? "").trim();
}

const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);
const VALID_STATUSES = ["Active", "Trial", "Inactive"];

async function downloadTemplate() {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "ClubOS Academy";

  const ws = wb.addWorksheet("Players", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  ws.columns = COLUMN_DEFS.map(({ header, width }) => ({ header, width }));

  ws.getRow(1).height = 22;
  ws.getRow(1).eachCell((cell) => {
    cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E7D52" } };
    cell.font      = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });

  SAMPLE_ROWS.forEach((data) => {
    const row = ws.addRow(data);
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F8F4" } };
    });
  });

  const info = wb.addWorksheet("Instructions");
  info.getColumn(1).width = 70;
  [
    "ACADEMY PLAYER BULK UPLOAD — INSTRUCTIONS",
    "",
    "1. Fill player data in the 'Players' sheet starting from row 2.",
    "2. Do NOT modify the header row (row 1).",
    "3. Required fields are marked with * in the header.",
    "4. Date format: DD-MM-YYYY  (e.g. 15-06-2015)",
    "5. Status values: Active | Trial | Inactive  (default: Active)",
    "6. ParentPhone / AlternatePhone: 10-digit number",
    "7. BatchName must exactly match an existing batch (case-insensitive).",
    "8. FeePlan must exactly match an existing fee plan (case-insensitive).",
    "9. MonthlyFee: numeric, e.g. 800",
    "10. PaidUpToMonth (required): MM-YYYY format, e.g. 03-2026. Sets the fee paid-up-to month.",
  ].forEach((text, i) => {
    const cell = info.getCell(`A${i + 1}`);
    cell.value = text;
    if (i === 0) cell.font = { bold: true, size: 13 };
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "academy-players-template.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

function validateRow(row, batchMap, feePlanNames) {
  const errors = [];
  const get = (key) => (row[`${key}*`] ?? row[key] ?? "").trim();

  const name          = get("FullName");
  const dob           = get("DateOfBirth");
  const parentName    = get("ParentName");
  const parentPhone   = get("ParentPhone");
  const altPhone      = get("AlternatePhone");
  const parentEmail   = get("ParentEmail");
  const address       = get("Address");
  const monthlyFee    = get("MonthlyFee");
  const feePlan       = get("FeePlan");
  const batchName     = get("BatchName");
  const admissionDate = get("AdmissionDate");
  const status        = get("Status");

  if (!name)           errors.push("FullName required");
  if (!dob)            errors.push("DateOfBirth required");
  else if (!DD_MM_YYYY.test(dob))               errors.push("DateOfBirth must be DD-MM-YYYY");
  else if (parseDDMMYYYY(dob) >= TODAY)         errors.push("DateOfBirth must be before today");

  if (!admissionDate)  errors.push("AdmissionDate required");
  else if (!DD_MM_YYYY.test(admissionDate))      errors.push("AdmissionDate must be DD-MM-YYYY");

  if (!parentName)     errors.push("ParentName required");
  if (!parentPhone)    errors.push("ParentPhone required");
  else if (!/^\d{10}$/.test(parentPhone))        errors.push("ParentPhone must be 10 digits");

  if (altPhone    && !/^\d{10}$/.test(altPhone)) errors.push("AlternatePhone must be 10 digits");
  if (parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail)) errors.push("ParentEmail invalid");

  if (!address)        errors.push("Address required");

  if (!monthlyFee)     errors.push("MonthlyFee required");
  else if (isNaN(Number(monthlyFee)) || Number(monthlyFee) < 0) errors.push("MonthlyFee must be ≥ 0");

  if (!feePlan)        errors.push("FeePlan required");
  else if (feePlanNames.length > 0 && !feePlanNames.some((f) => f.toLowerCase() === feePlan.toLowerCase()))
    errors.push(`FeePlan '${feePlan}' not found`);

  if (!batchName)      errors.push("BatchName required");
  else if (batchMap.size > 0 && !batchMap.has(batchName.toLowerCase()))
    errors.push(`Batch '${batchName}' not found`);

  if (status && !VALID_STATUSES.includes(status)) errors.push("Status must be Active, Trial, or Inactive");

  const paidUpTo = get("PaidUpToMonth");
  if (!paidUpTo) {
    errors.push("PaidUpToMonth required");
  } else {
    const parts = paidUpTo.split("-");
    const mm = parseInt(parts[0], 10);
    const yyyy = parseInt(parts[1], 10);
    if (parts.length !== 2 || isNaN(mm) || mm < 1 || mm > 12 || isNaN(yyyy) || yyyy < 2000)
      errors.push("PaidUpToMonth must be MM-YYYY (e.g. 03-2026)");
  }

  return {
    errors,
    parsed: { name, dob, parentName, parentPhone, batchName, feePlan, admissionDate, status, paidUpTo },
  };
}

function rowToPayload(row) {
  const get = (key) => (row[`${key}*`] ?? row[key] ?? "").trim();
  return {
    fullName:       get("FullName"),
    dateOfBirth:    toISODate(get("DateOfBirth")),
    admissionDate:  toISODate(get("AdmissionDate")),
    monthlyFee:     get("MonthlyFee"),
    parentName:     get("ParentName")     || null,
    parentPhone:    get("ParentPhone")    || null,
    alternatePhone: get("AlternatePhone") || null,
    parentEmail:    get("ParentEmail")    || null,
    address:        get("Address")        || null,
    batchName:      get("BatchName"),
    feePlan:        get("FeePlan")        || null,
    status:         get("Status")         || "Active",
    notes:          get("Notes")          || null,
    paidUpToMonth:  get("PaidUpToMonth")  || null,
  };
}

const TABLE_COLS = [
  { key: "row",     label: "#",             defaultWidth: 40  },
  { key: "name",    label: "Full Name",     defaultWidth: 150 },
  { key: "dob",     label: "Date of Birth", defaultWidth: 100 },
  { key: "parent",  label: "Parent Name",   defaultWidth: 130 },
  { key: "phone",   label: "Parent Phone",  defaultWidth: 110 },
  { key: "batch",   label: "Batch",         defaultWidth: 115 },
  { key: "plan",    label: "Fee Plan",      defaultWidth: 105 },
  { key: "admDate", label: "Adm. Date",     defaultWidth: 100 },
  { key: "status",  label: "Status",        defaultWidth: 80  },
  { key: "result",  label: "",              defaultWidth: 50  },
];

const TOTAL_COLS = TABLE_COLS.length;

const MIN_COL_WIDTH = 40;

export default function BulkUploadDialog({ open, onClose, batches, feePlans, onUploaded }) {
  const [parsedRows,    setParsedRows]    = useState([]);
  const [parseError,    setParseError]    = useState("");
  const [uploading,     setUploading]     = useState(false);
  const [result,        setResult]        = useState(null);
  const [expandedRows,  setExpandedRows]  = useState(new Set());
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [colWidths,     setColWidths]     = useState(
    () => Object.fromEntries(TABLE_COLS.map((c) => [c.key, c.defaultWidth]))
  );

  const fileInputRef   = useRef(null);
  const resizingRef    = useRef(null); // { key, startX, startWidth }

  const batchMap     = new Map(batches.map((b) => [b.name.toLowerCase(), b.id]));
  const feePlanNames = feePlans.map((f) => f.name);

  useEffect(() => {
    setExpandedRows(new Set(parsedRows.filter((r) => !r.valid).map((r) => r.rowNum)));
  }, [parsedRows]);

  const onMouseMove = useCallback((e) => {
    if (!resizingRef.current) return;
    const { key, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;
    setColWidths((prev) => ({ ...prev, [key]: Math.max(MIN_COL_WIDTH, startWidth + delta) }));
  }, []);

  const onMouseUp = useCallback(() => {
    resizingRef.current = null;
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  function startResize(e, key) {
    e.preventDefault();
    resizingRef.current = { key, startX: e.clientX, startWidth: colWidths[key] };
  }

  function toggleRow(rowNum) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(rowNum) ? next.delete(rowNum) : next.add(rowNum);
      return next;
    });
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setParseError("");
    setParsedRows([]);
    setShowErrorsOnly(false);

    try {
      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(await file.arrayBuffer());

      const ws = wb.worksheets[0];
      if (!ws) { setParseError("No worksheet found in the file."); return; }

      const headers = [];
      ws.getRow(1).eachCell({ includeEmpty: false }, (cell, col) => {
        headers[col - 1] = String(cell.value ?? "").trim();
      });

      const dataRows = [];
      ws.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const rowData = {};
        let hasData = false;
        row.eachCell({ includeEmpty: true }, (cell, col) => {
          const header = headers[col - 1];
          if (!header) return;
          const val = excelDateToString(cell.value);
          rowData[header] = val;
          if (val) hasData = true;
        });
        if (hasData) dataRows.push(rowData);
      });

      if (dataRows.length === 0) {
        setParseError("No data rows found. Fill in rows below the header and re-upload.");
        return;
      }

      setParsedRows(
        dataRows.map((row, i) => {
          const { errors, parsed } = validateRow(row, batchMap, feePlanNames);
          return { rowNum: i + 1, raw: row, parsed, errors, valid: errors.length === 0 };
        })
      );
    } catch (err) {
      setParseError(`Failed to read Excel file: ${err.message}`);
    }
  }

  async function handleUpload() {
    const validRows = parsedRows.filter((r) => r.valid).map((r) => rowToPayload(r.raw));
    setUploading(true);
    try {
      const res = await academyService.bulkUploadPlayers(validRows);
      setResult(res);
      if (res.successCount > 0) onUploaded();
    } catch (err) {
      setParseError(err?.response?.data?.detail ?? "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  function handleClose() {
    setParsedRows([]);
    setParseError("");
    setResult(null);
    setExpandedRows(new Set());
    setShowErrorsOnly(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  }

  const validCount  = parsedRows.filter((r) => r.valid).length;
  const errorCount  = parsedRows.filter((r) => !r.valid).length;
  const displayRows = showErrorsOnly ? parsedRows.filter((r) => !r.valid) : parsedRows;
  const totalWidth  = Object.values(colWidths).reduce((a, b) => a + b, 0);

  const headerCellSx = {
    position: "relative",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    userSelect: "none",
    fontWeight: 600,
    fontSize: "0.7rem",
    px: 1,
    py: 0.75,
  };

  const resizeHandleSx = {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: "5px",
    cursor: "col-resize",
    zIndex: 1,
    "&:hover": { bgcolor: "primary.main", opacity: 0.5 },
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <SoftTypography variant="h6" fontWeight="bold">
          Bulk Upload Players
        </SoftTypography>
        <SoftTypography variant="caption" color="secondary">
          Download the Excel template, fill in player details, then upload the file.
        </SoftTypography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Step 1 */}
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <SoftBox>
            <SoftTypography variant="button" fontWeight="medium">
              Step 1 — Download Template
            </SoftTypography>
            <SoftTypography variant="caption" color="secondary" display="block">
              Fill in the Excel template. Required fields are marked with *. Date format: DD-MM-YYYY.
            </SoftTypography>
          </SoftBox>
          <SoftButton variant="outlined" color="primary" size="small" onClick={downloadTemplate}>
            <Icon sx={{ mr: 0.5 }}>download</Icon>
            Download Template
          </SoftButton>
        </SoftBox>

        <Divider />

        {/* Step 2 */}
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" mt={2} mb={2}>
          <SoftBox>
            <SoftTypography variant="button" fontWeight="medium">
              Step 2 — Select Your Excel File
            </SoftTypography>
            <SoftTypography variant="caption" color="secondary" display="block">
              The file will be validated before upload.
            </SoftTypography>
          </SoftBox>
          <SoftButton
            variant="gradient"
            color="dark"
            size="small"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon sx={{ mr: 0.5 }}>upload_file</Icon>
            Choose File
          </SoftButton>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </SoftBox>

        {parseError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {parseError}
          </Alert>
        )}

        {result && (
          <Alert severity={result.failureCount > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
            {result.successCount} player{result.successCount !== 1 ? "s" : ""} created successfully.
            {result.failureCount > 0 && ` ${result.failureCount} row(s) failed.`}
          </Alert>
        )}

        {parsedRows.length > 0 && (
          <>
            <SoftBox display="flex" gap={1} mb={1.5} alignItems="center" flexWrap="wrap">
              <Chip label={`${validCount} valid`} color="success" size="small" />
              {errorCount > 0 && (
                <Chip label={`${errorCount} with errors`} color="error" size="small" />
              )}
              {errorCount > 0 && (
                <Chip
                  label={showErrorsOnly ? "Show all rows" : "Show errors only"}
                  icon={<Icon style={{ fontSize: 14 }}>{showErrorsOnly ? "list" : "filter_alt"}</Icon>}
                  onClick={() => setShowErrorsOnly((v) => !v)}
                  variant={showErrorsOnly ? "filled" : "outlined"}
                  color="error"
                  size="small"
                  sx={{ cursor: "pointer" }}
                />
              )}
              <SoftTypography variant="caption" color="secondary" sx={{ ml: "auto" }}>
                Drag column edges to resize
              </SoftTypography>
            </SoftBox>

            <TableContainer sx={{ maxHeight: 420, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Table
                size="small"
                stickyHeader
                sx={{ tableLayout: "fixed", width: totalWidth, minWidth: totalWidth }}
              >
                <colgroup>
                  {TABLE_COLS.map((col) => (
                    <col key={col.key} style={{ width: colWidths[col.key] }} />
                  ))}
                </colgroup>

                <TableHead>
                  <TableRow>
                    {TABLE_COLS.map((col) => (
                      <TableCell
                        key={col.key}
                        sx={{ ...headerCellSx, width: colWidths[col.key], minWidth: colWidths[col.key], maxWidth: colWidths[col.key] }}
                      >
                        {col.label}
                        {col.key !== "result" && (
                          <Box sx={resizeHandleSx} onMouseDown={(e) => startResize(e, col.key)} />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {displayRows.map((row) => {
                    const isExpanded = expandedRows.has(row.rowNum);
                    return [
                      <TableRow
                        key={`data-${row.rowNum}`}
                        sx={{ bgcolor: row.valid ? "rgba(76,175,80,0.04)" : "rgba(244,67,54,0.06)" }}
                      >
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.row,     maxWidth: colWidths.row,     overflow: "hidden" }}>{row.rowNum}</TableCell>
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.name,    maxWidth: colWidths.name,    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {row.parsed.name || "—"}
                        </TableCell>
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.dob,     maxWidth: colWidths.dob,     whiteSpace: "nowrap" }}>
                          {row.parsed.dob || "—"}
                        </TableCell>
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.parent,  maxWidth: colWidths.parent,  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {row.parsed.parentName || "—"}
                        </TableCell>
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.phone,   maxWidth: colWidths.phone,   whiteSpace: "nowrap" }}>
                          {row.parsed.parentPhone || "—"}
                        </TableCell>
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.batch,   maxWidth: colWidths.batch,   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {row.parsed.batchName || "—"}
                        </TableCell>
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.plan,    maxWidth: colWidths.plan,    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {row.parsed.feePlan || "—"}
                        </TableCell>
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.admDate, maxWidth: colWidths.admDate, whiteSpace: "nowrap" }}>
                          {row.parsed.admissionDate || "—"}
                        </TableCell>
                        <TableCell sx={{ px: 1, fontSize: "0.75rem", width: colWidths.status,  maxWidth: colWidths.status,  whiteSpace: "nowrap" }}>
                          {row.parsed.status || "Active"}
                        </TableCell>
                        <TableCell sx={{ px: 0.5, textAlign: "center", width: colWidths.result, maxWidth: colWidths.result }}>
                          {row.valid ? (
                            <Icon sx={{ color: "success.main", fontSize: 18 }}>check_circle</Icon>
                          ) : (
                            <IconButton size="small" onClick={() => toggleRow(row.rowNum)} sx={{ p: 0.25 }}>
                              <Icon sx={{ color: "error.main", fontSize: 18 }}>
                                {isExpanded ? "expand_less" : "error"}
                              </Icon>
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>,

                      !row.valid && (
                        <TableRow key={`err-${row.rowNum}`} sx={{ bgcolor: "rgba(244,67,54,0.04)" }}>
                          <TableCell
                            colSpan={TOTAL_COLS}
                            sx={{ py: 0, border: 0 }}
                          >
                            <Collapse in={isExpanded} unmountOnExit>
                              <SoftBox
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  px: 2,
                                  py: 0.75,
                                  borderLeft: "3px solid",
                                  borderColor: "error.main",
                                  mb: 0.5,
                                }}
                              >
                                {row.errors.map((e, i) => (
                                  <Chip key={i} label={e} size="small" color="error" variant="outlined" />
                                ))}
                              </SoftBox>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      ),
                    ];
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <SoftButton variant="text" color="secondary" onClick={handleClose}>
          Close
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="success"
          onClick={handleUpload}
          disabled={validCount === 0 || uploading || !!result}
        >
          {uploading ? "Uploading..." : `Upload ${validCount} Player${validCount !== 1 ? "s" : ""}`}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

BulkUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  batches: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })).isRequired,
  feePlans: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  onUploaded: PropTypes.func.isRequired,
};
